'use client';

import { useState } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, X, Calendar, MessageCircle } from 'lucide-react';

// Artwork piece type
type ArtworkPiece = {
  name: string;
  image: string;
  title: string;
  quote: string;
  date: string;
};

// Timeline period type
type TimelinePeriod = {
  label: string;
  pieces: ArtworkPiece[];
};

// Gallery data organized by time periods
const gallery = {
  childName: 'Ben',
  year: 2025,
  title: "Ben's 2025 Collection",
  totalArtworks: 12,
  periods: [
    {
      label: 'January - February',
      pieces: [
        {
          name: 'snowman',
          image: '/marketing/gallery/winter-snowman.png',
          title: 'My Snowman Friend',
          quote: "This is Frosty! He has a carrot nose and he's my best friend. We play together when it snows!",
          date: 'January 15, 2025'
        },
        {
          name: 'penguin',
          image: '/marketing/gallery/winter-penguin.png',
          title: 'Happy Penguin',
          quote: "Penguins live where it's super cold! This one is wearing a scarf so he stays warm.",
          date: 'January 28, 2025'
        },
        {
          name: 'mittens',
          image: '/marketing/gallery/winter-mittens.png',
          title: 'Cozy Mittens',
          quote: "These are magic mittens! They keep your hands warm and they have rainbow stripes.",
          date: 'February 5, 2025'
        },
      ],
    },
    {
      label: 'March - April',
      pieces: [
        {
          name: 'flowers',
          image: '/marketing/gallery/spring-flowers.png',
          title: 'Garden Party',
          quote: "I drew all the flowers in grandma's garden! The sun is smiling because the flowers are so pretty.",
          date: 'March 12, 2025'
        },
        {
          name: 'butterfly',
          image: '/marketing/gallery/spring-butterfly.png',
          title: 'Rainbow Butterfly',
          quote: "This butterfly has ALL the colors! She flies around and makes everyone happy.",
          date: 'April 3, 2025'
        },
        {
          name: 'rainbow',
          image: '/marketing/gallery/spring-rainbow.png',
          title: 'After the Rain',
          quote: "Rainbows come out after it rains. I counted all seven colors - red, orange, yellow, green, blue, and purple!",
          date: 'April 20, 2025'
        },
      ],
    },
    {
      label: 'June - August',
      pieces: [
        {
          name: 'beach',
          image: '/marketing/gallery/summer-beach.png',
          title: 'Beach Day',
          quote: "We went to the beach! I made a big sandcastle and the waves came really close!",
          date: 'June 15, 2025'
        },
        {
          name: 'ice-cream',
          image: '/marketing/gallery/summer-ice-cream.png',
          title: 'Yummy Ice Cream',
          quote: "Three scoops! Strawberry, chocolate, and banana. It's the best ice cream ever!",
          date: 'July 4, 2025'
        },
        {
          name: 'fish',
          image: '/marketing/gallery/summer-fish.png',
          title: 'Swimming Fish',
          quote: "This fish lives in the ocean with all his bubble friends. He's orange and blue and super fast!",
          date: 'August 8, 2025'
        },
      ],
    },
    {
      label: 'September - October',
      pieces: [
        {
          name: 'pumpkin',
          image: '/marketing/gallery/fall-pumpkin.png',
          title: 'Pumpkin Patch',
          quote: "We picked the biggest pumpkin! It's going to be a jack-o-lantern with a smiley face.",
          date: 'September 22, 2025'
        },
        {
          name: 'tree',
          image: '/marketing/gallery/fall-tree.png',
          title: 'Falling Leaves',
          quote: "The tree is changing colors! Red and orange and yellow leaves are falling down like rain.",
          date: 'October 10, 2025'
        },
        {
          name: 'apple',
          image: '/marketing/gallery/fall-apple.png',
          title: 'Apple Picking',
          quote: "A little worm lives in this apple! He peeked out to say hi. Don't worry, he's friendly!",
          date: 'October 25, 2025'
        },
      ],
    },
  ] as TimelinePeriod[],
};

// Truncate quote for preview
function truncateQuote(quote: string, maxLength: number = 50): string {
  if (quote.length <= maxLength) return quote;
  return quote.substring(0, maxLength).trim() + '...';
}

/**
 * Art Gallery Section - Apple Photos Style Timeline
 * Vertical timeline with artwork grid and inline insights
 */
export function ArtGallery() {
  const [selectedArtwork, setSelectedArtwork] = useState<ArtworkPiece | null>(null);

  return (
    <section id="art-gallery" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Palette className="w-6 h-6 text-terracotta-500" />
            <span className="text-terracotta-500 font-medium text-sm uppercase tracking-wide">
              Beautifully Organized
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-charcoal-600 mb-4">
            A Gallery That Grows With Them
          </h2>
          <p className="text-taupe-500 text-base md:text-lg max-w-2xl mx-auto">
            Every masterpiece, organized by time. Watch your child&apos;s creativity unfold through the year.
          </p>
        </motion.div>

        {/* Year Header - Apple Photos Style */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-2">
            <div className="w-10 h-10 rounded-full bg-charcoal-800 flex items-center justify-center">
              <span className="text-white font-semibold text-sm">B</span>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-charcoal-700">{gallery.title}</h3>
              <p className="text-sm text-taupe-400">{gallery.totalArtworks} artworks</p>
            </div>
          </div>
        </motion.div>

        {/* Timeline */}
        <div className="space-y-10">
          {gallery.periods.map((period, periodIndex) => (
            <motion.div
              key={period.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: periodIndex * 0.1 }}
            >
              {/* Period Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-1 h-6 bg-terracotta-400 rounded-full" />
                <h4 className="text-sm font-semibold text-charcoal-600 uppercase tracking-wide">
                  {period.label}
                </h4>
                <span className="text-xs text-taupe-400">
                  {period.pieces.length} artworks
                </span>
              </div>

              {/* Artwork Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {period.pieces.map((piece, pieceIndex) => (
                  <motion.div
                    key={piece.name}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.1 + pieceIndex * 0.05 }}
                    onClick={() => setSelectedArtwork(piece)}
                    className="group cursor-pointer"
                  >
                    {/* Artwork Image */}
                    <div className="relative aspect-square rounded-xl overflow-hidden bg-cream-100 shadow-sm group-hover:shadow-lg transition-shadow mb-3">
                      <Image
                        src={piece.image}
                        alt={piece.title}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>

                    {/* Artwork Info */}
                    <div className="space-y-1">
                      <h5 className="font-medium text-charcoal-700 text-sm group-hover:text-terracotta-600 transition-colors">
                        {piece.title}
                      </h5>
                      <p className="text-xs text-taupe-400 leading-relaxed">
                        &ldquo;{truncateQuote(piece.quote)}&rdquo;
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Caption */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center text-sm text-taupe-400 mt-12 max-w-lg mx-auto"
        >
          Organize artwork by month, school year, or theme. TinyChapters helps you build a beautiful archive of your child&apos;s creative journey.
        </motion.p>
      </div>

      {/* Artwork Detail Modal */}
      <AnimatePresence>
        {selectedArtwork && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setSelectedArtwork(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedArtwork(null)}
                className="absolute top-4 right-4 z-10 w-8 h-8 bg-white/90 rounded-full shadow-md flex items-center justify-center text-charcoal-500 hover:bg-white hover:text-charcoal-700 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Artwork Image */}
              <div className="relative aspect-square bg-cream-50">
                <Image
                  src={selectedArtwork.image}
                  alt={selectedArtwork.title}
                  fill
                  className="object-contain p-4"
                />
              </div>

              {/* Artwork Details */}
              <div className="p-6 space-y-4">
                {/* Title */}
                <h3 className="text-xl font-serif font-semibold text-charcoal-600">
                  {selectedArtwork.title}
                </h3>

                {/* Quote - Speech Bubble Style */}
                <div className="relative bg-terracotta-50 rounded-xl p-4 border border-terracotta-100">
                  <div className="flex gap-3">
                    <MessageCircle className="w-5 h-5 text-terracotta-400 flex-shrink-0 mt-0.5" />
                    <p className="text-charcoal-600 italic leading-relaxed">
                      &ldquo;{selectedArtwork.quote}&rdquo;
                    </p>
                  </div>
                  <p className="text-xs text-taupe-400 mt-2 ml-8">
                    - {gallery.childName}, age 5
                  </p>
                </div>

                {/* Date */}
                <div className="flex items-center gap-2 text-sm text-taupe-500">
                  <Calendar className="w-4 h-4" />
                  <span>{selectedArtwork.date}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
